import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  MapPin, Plus, Building2, MoreVertical, Pencil, Trash2,
  ChevronRight, ChevronDown, Loader2, LayoutGrid
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import LocationAutocomplete from "../components/LocationAutocomplete";

const SPORT_OPTIONS = [
  // Field sports
  { value: "soccer", label: "Soccer", type: "field" },
  { value: "football", label: "Football", type: "field" },
  { value: "baseball", label: "Baseball", type: "field" },
  { value: "softball", label: "Softball", type: "field" },
  { value: "lacrosse", label: "Lacrosse", type: "field" },
  { value: "field_hockey", label: "Field Hockey", type: "field" },
  { value: "rugby", label: "Rugby", type: "field" },
  // Court sports
  { value: "basketball", label: "Basketball", type: "court" },
  { value: "tennis", label: "Tennis", type: "court" },
  { value: "volleyball", label: "Volleyball", type: "court" },
  { value: "pickleball", label: "Pickleball", type: "court" },
  { value: "badminton", label: "Badminton", type: "court" },
];

const AdminFacilities = ({ embedded = false }) => {
  const { token } = useAuth();
  const [locations, setLocations] = useState([]);
  const [sportConfig, setSportConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [expandedLocations, setExpandedLocations] = useState({});
  
  // Location dialog
  const [locationDialog, setLocationDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({ name: "", address: "", notes: "", latitude: null, longitude: null, place_id: null });
  const [savingLocation, setSavingLocation] = useState(false);
  
  // Facility dialog
  const [facilityDialog, setFacilityDialog] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [facilityForm, setFacilityForm] = useState({ name: "", sport: "", size: "", notes: "" });
  const [savingFacility, setSavingFacility] = useState(false);

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [locRes, configRes] = await Promise.all([
        axios.get(`${API}/facilities/locations`, { headers }),
        axios.get(`${API}/facilities/config`, { headers })
      ]);
      
      setSportConfig(configRes.data);
      
      // Auto-expand locations with facilities and fetch their facilities
      const expanded = {};
      const locationsWithFacilities = await Promise.all(
        locRes.data.map(async (loc) => {
          if (loc.facility_count > 0) {
            expanded[loc.location_id] = true;
            // Fetch facilities for this location
            try {
              const facRes = await axios.get(`${API}/facilities/locations/${loc.location_id}`, { headers });
              return { ...loc, facilities: facRes.data.facilities };
            } catch (err) {
              return loc;
            }
          }
          return loc;
        })
      );
      
      setLocations(locationsWithFacilities);
      setExpandedLocations(expanded);
    } catch (error) {
      console.error("Error fetching facilities:", error);
      setLoadError(error.response?.data?.detail || "We couldn't load your locations and facilities. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationFacilities = async (locationId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/facilities/locations/${locationId}`, { headers });
      setLocations(prev => prev.map(loc => 
        loc.location_id === locationId ? { ...loc, facilities: response.data.facilities } : loc
      ));
    } catch (error) {
      console.error("Error fetching location facilities:", error);
    }
  };

  const toggleLocation = async (locationId) => {
    const isExpanding = !expandedLocations[locationId];
    setExpandedLocations(prev => ({ ...prev, [locationId]: isExpanding }));
    
    if (isExpanding) {
      const location = locations.find(l => l.location_id === locationId);
      if (!location.facilities) {
        await fetchLocationFacilities(locationId);
      }
    }
  };

  // Location handlers
  const openLocationDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setLocationForm({ 
        name: location.name, 
        address: location.address || "", 
        notes: location.notes || "",
        latitude: location.latitude || null,
        longitude: location.longitude || null,
        place_id: location.place_id || null
      });
    } else {
      setEditingLocation(null);
      setLocationForm({ name: "", address: "", notes: "", latitude: null, longitude: null, place_id: null });
    }
    setLocationDialog(true);
  };

  const handlePlaceSelect = (place) => {
    setLocationForm(prev => ({
      ...prev,
      address: place.address || place.full_text,
      latitude: place.latitude,
      longitude: place.longitude,
      place_id: place.place_id,
      // Auto-fill name if empty
      name: prev.name || place.name || ""
    }));
  };

  const saveLocation = async () => {
    if (!locationForm.name.trim()) {
      toast.error("Location name is required");
      return;
    }
    
    setSavingLocation(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingLocation) {
        await axios.put(`${API}/facilities/locations/${editingLocation.location_id}`, locationForm, { headers });
        toast.success("Location updated");
      } else {
        await axios.post(`${API}/facilities/locations`, locationForm, { headers });
        toast.success("Location created");
      }
      setLocationDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save location");
    } finally {
      setSavingLocation(false);
    }
  };

  // Facility handlers
  const openFacilityDialog = (locationId, facility = null) => {
    setSelectedLocationId(locationId);
    if (facility) {
      setEditingFacility(facility);
      setFacilityForm({ 
        name: facility.name, 
        sport: facility.sport, 
        size: facility.size || "", 
        notes: facility.notes || "" 
      });
    } else {
      setEditingFacility(null);
      setFacilityForm({ name: "", sport: "", size: "", notes: "" });
    }
    setFacilityDialog(true);
  };

  const saveFacility = async () => {
    if (!facilityForm.name.trim() || !facilityForm.sport) {
      toast.error("Name and sport are required");
      return;
    }
    
    // Check for duplicate name within the same location
    const location = locations.find(l => l.location_id === selectedLocationId);
    if (location?.facilities) {
      const duplicateName = location.facilities.some(f => 
        f.name.toLowerCase() === facilityForm.name.trim().toLowerCase() &&
        (!editingFacility || f.facility_id !== editingFacility.facility_id)
      );
      if (duplicateName) {
        toast.error(`A field/court named "${facilityForm.name.trim()}" already exists at this location`);
        return;
      }
    }
    
    setSavingFacility(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = { ...facilityForm, location_id: selectedLocationId };
      
      if (editingFacility) {
        await axios.put(`${API}/facilities/facilities/${editingFacility.facility_id}`, facilityForm, { headers });
        toast.success("Facility updated");
      } else {
        await axios.post(`${API}/facilities/facilities`, payload, { headers });
        toast.success("Facility created");
      }
      setFacilityDialog(false);
      
      // Refresh the specific location's facilities without resetting all data
      await fetchLocationFacilities(selectedLocationId);
      
      // Update the facility count for this location
      setLocations(prev => prev.map(loc => 
        loc.location_id === selectedLocationId 
          ? { ...loc, facility_count: (loc.facility_count || 0) + (editingFacility ? 0 : 1) }
          : loc
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save facility");
    } finally {
      setSavingFacility(false);
    }
  };

  // Delete handlers
  const confirmDelete = (type, item) => {
    setDeleteTarget({ type, item });
    setDeleteDialog(true);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (deleteTarget.type === "location") {
        await axios.delete(`${API}/facilities/locations/${deleteTarget.item.location_id}`, { headers });
        toast.success("Location deleted");
        // Remove location from state
        setLocations(prev => prev.filter(loc => loc.location_id !== deleteTarget.item.location_id));
      } else {
        const facilityLocationId = deleteTarget.item.location_id;
        await axios.delete(`${API}/facilities/facilities/${deleteTarget.item.facility_id}`, { headers });
        toast.success("Facility deleted");
        // Update the specific location's facilities and count
        setLocations(prev => prev.map(loc => {
          if (loc.location_id === facilityLocationId) {
            return {
              ...loc,
              facility_count: Math.max(0, (loc.facility_count || 1) - 1),
              facilities: loc.facilities?.filter(f => f.facility_id !== deleteTarget.item.facility_id)
            };
          }
          return loc;
        }));
      }
      setDeleteDialog(false);
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const getSportConfig = (sport) => {
    return sportConfig[sport?.toLowerCase()] || { type: "field", sizes: [] };
  };

  const getFacilityTypeLabel = (sport) => {
    const config = getSportConfig(sport);
    return config.type === "court" ? "Court" : "Field";
  };

  const getSizeOptions = (sport) => {
    const config = getSportConfig(sport);
    return config.sizes || [];
  };

  if (loading) {
    const loadingState = <div className="flex h-64 items-center justify-center" role="status" aria-label="Loading facilities"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /><span className="sr-only">Loading facilities</span></div>;
    return embedded ? loadingState : <AdminLayout>{loadingState}</AdminLayout>;
  }

  if (loadError) {
    const errorState = (
      <section className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-rose-50 p-6" role="alert">
        <h1 className="text-lg font-semibold text-rose-950">Facilities unavailable</h1>
        <p className="mt-2 text-sm leading-6 text-rose-800">{loadError}</p>
        <Button onClick={fetchData} className="mt-4 rounded-xl bg-rose-700 hover:bg-rose-800">Try again</Button>
      </section>
    );
    return embedded ? errorState : <AdminLayout>{errorState}</AdminLayout>;
  }

  const content = (
    <>
      <div className="space-y-6">
        {/* Header */}
        <EditorialPageHeader
          eyebrow="Operations"
          title="Facilities"
          description="Manage locations, fields, courts, and the spaces your schedule depends on."
          headingId="facilities-heading"
          aside={<Button
            onClick={() => openLocationDialog()}
            className="w-full rounded-full bg-[#b7f34a] text-[#102a27] hover:bg-[#c9ff63] sm:w-auto"
            data-testid="add-location-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </Button>}
        />

        {/* Locations List */}
        {locations.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No locations yet</h3>
            <p className="text-slate-500 mb-6">Add a location to start managing your fields and courts</p>
            <Button 
              onClick={() => openLocationDialog()}
              className="bg-slate-900 hover:bg-slate-800 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Location
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {locations.map((location) => (
              <div 
                key={location.location_id} 
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
                data-testid={`location-${location.location_id}`}
              >
                {/* Location Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggleLocation(location.location_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">{location.name}</h3>
                      {location.address && (
                        <p className="text-sm text-slate-500">{location.address}</p>
                      )}
                    </div>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-lg">
                      {location.facility_count} {location.facility_count === 1 ? 'facility' : 'facilities'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); openLocationDialog(location); }}
                          className="rounded-lg"
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit Location
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); confirmDelete("location", location); }}
                          className="rounded-lg text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Location
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {expandedLocations[location.location_id] ? (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Facilities */}
                {expandedLocations[location.location_id] && (
                  <div className="border-t border-slate-100 bg-slate-50 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-slate-700">Fields & Courts</h4>
                      <Button 
                        size="sm" 
                        onClick={() => openFacilityDialog(location.location_id)}
                        className="bg-slate-900 hover:bg-slate-800 rounded-lg h-8"
                        data-testid={`add-facility-${location.location_id}`}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Field/Court
                      </Button>
                    </div>
                    
                    {(!location.facilities || location.facilities.length === 0) ? (
                      <div className="text-center py-8 text-slate-500">
                        <LayoutGrid className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">No fields or courts at this location</p>
                      </div>
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {location.facilities.map((facility) => (
                          <div 
                            key={facility.facility_id}
                            className="bg-white border border-slate-200 rounded-xl p-4"
                            data-testid={`facility-${facility.facility_id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                  facility.facility_type === 'court' ? 'bg-orange-50' : 'bg-green-50'
                                }`}>
                                  <Building2 className={`w-4 h-4 ${
                                    facility.facility_type === 'court' ? 'text-orange-600' : 'text-green-600'
                                  }`} />
                                </div>
                                <div>
                                  <h5 className="font-medium text-slate-900 text-sm">{facility.name}</h5>
                                  <p className="text-xs text-slate-500 capitalize">
                                    {facility.sport} {facility.size && `• ${facility.size}`}
                                  </p>
                                </div>
                              </div>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded">
                                    <MoreVertical className="w-3 h-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="rounded-xl">
                                  <DropdownMenuItem 
                                    onClick={() => openFacilityDialog(location.location_id, facility)}
                                    className="rounded-lg"
                                  >
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => confirmDelete("facility", facility)}
                                    className="rounded-lg text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            {facility.notes && (
                              <p className="text-xs text-slate-500 mt-2 line-clamp-2">{facility.notes}</p>
                            )}
                            <div className="mt-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                facility.facility_type === 'court' 
                                  ? 'bg-orange-100 text-orange-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {facility.facility_type === 'court' ? 'Court' : 'Field'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Location Dialog */}
      <Dialog open={locationDialog} onOpenChange={setLocationDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>{editingLocation ? "Edit Location" : "Add Location"}</DialogTitle>
            <DialogDescription>
              {editingLocation ? "Update the location details" : "Add a new location for your fields and courts"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="loc-name">Name *</Label>
              <Input
                id="loc-name"
                placeholder="e.g., Springfield Sports Complex"
                value={locationForm.name}
                onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                className="rounded-xl"
                data-testid="location-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="loc-address">Address</Label>
              <LocationAutocomplete
                value={locationForm.address}
                onChange={(value) => setLocationForm({ ...locationForm, address: value })}
                onPlaceSelect={handlePlaceSelect}
                placeholder="Search for address..."
                className="rounded-xl"
              />
              {locationForm.latitude && locationForm.longitude && (
                <p className="text-xs text-slate-500 mt-1">
                  📍 Coordinates: {locationForm.latitude.toFixed(6)}, {locationForm.longitude.toFixed(6)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="loc-notes">Notes</Label>
              <Textarea
                id="loc-notes"
                placeholder="Any additional details about this location..."
                value={locationForm.notes}
                onChange={(e) => setLocationForm({ ...locationForm, notes: e.target.value })}
                className="rounded-xl"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLocationDialog(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={saveLocation} 
              disabled={savingLocation}
              className="bg-slate-900 hover:bg-slate-800 rounded-xl"
              data-testid="save-location-btn"
            >
              {savingLocation ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingLocation ? "Update" : "Add"} Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Facility Dialog */}
      <Dialog open={facilityDialog} onOpenChange={setFacilityDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingFacility ? "Edit" : "Add"} {facilityForm.sport ? getFacilityTypeLabel(facilityForm.sport) : "Field/Court"}
            </DialogTitle>
            <DialogDescription>
              {editingFacility ? "Update the facility details" : "Add a new field or court to this location"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="fac-sport">Sport *</Label>
              <Select
                value={facilityForm.sport}
                onValueChange={(value) => setFacilityForm({ ...facilityForm, sport: value, size: "" })}
              >
                <SelectTrigger className="rounded-xl" data-testid="facility-sport-select">
                  <SelectValue placeholder="Select a sport" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500">Field Sports</div>
                  {SPORT_OPTIONS.filter(s => s.type === "field").map(sport => (
                    <SelectItem key={sport.value} value={sport.value} className="rounded-lg">
                      {sport.label}
                    </SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 mt-2">Court Sports</div>
                  {SPORT_OPTIONS.filter(s => s.type === "court").map(sport => (
                    <SelectItem key={sport.value} value={sport.value} className="rounded-lg">
                      {sport.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="fac-name">Name *</Label>
              <Input
                id="fac-name"
                placeholder={`e.g., ${facilityForm.sport ? getFacilityTypeLabel(facilityForm.sport) : "Field"} 1`}
                value={facilityForm.name}
                onChange={(e) => setFacilityForm({ ...facilityForm, name: e.target.value })}
                className="rounded-xl"
                data-testid="facility-name-input"
              />
            </div>

            {facilityForm.sport && getSizeOptions(facilityForm.sport).length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="fac-size">Size</Label>
                <Select
                  value={facilityForm.size}
                  onValueChange={(value) => setFacilityForm({ ...facilityForm, size: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="facility-size-select">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {getSizeOptions(facilityForm.sport).map(size => (
                      <SelectItem key={size} value={size} className="rounded-lg">
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="fac-notes">Notes</Label>
              <Textarea
                id="fac-notes"
                placeholder="Any additional details..."
                value={facilityForm.notes}
                onChange={(e) => setFacilityForm({ ...facilityForm, notes: e.target.value })}
                className="rounded-xl"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFacilityDialog(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={saveFacility} 
              disabled={savingFacility}
              className="bg-slate-900 hover:bg-slate-800 rounded-xl"
              data-testid="save-facility-btn"
            >
              {savingFacility ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingFacility ? "Update" : "Add"} {facilityForm.sport ? getFacilityTypeLabel(facilityForm.sport) : "Facility"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.type === "location" ? "Location" : "Facility"}</DialogTitle>
            <DialogDescription>
              {deleteTarget?.type === "location" 
                ? "This will delete the location and all fields/courts at this location. This action cannot be undone."
                : "Are you sure you want to delete this facility? This action cannot be undone."
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={executeDelete}
              disabled={deleting}
              className="rounded-xl"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return embedded ? content : <AdminLayout title="Facilities">{content}</AdminLayout>;
};

export default AdminFacilities;
